import React, { useEffect, useState } from "react";
import store from "../../redux/store";
import {
  Box,
  Card,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  SimpleGrid,
  Spacer,
  Text,
  Badge,
  Avatar,
  Button,
  Divider,
  Icon,
} from "@chakra-ui/react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiSearch,
  FiPhone,
  FiMail,
  FiMapPin,
  FiArrowRight,
  FiUser,
} from "react-icons/fi";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { get_customer_list } from "../../redux/customerSlice";
import GasCylinderLoader from "../../components/GasCylinderLoader";

const CustomerList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState(10);
  const [page, setpage] = useState(1);
  const [initialValue, setInitialValue] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const { customer_list, total_customer_count, loading } = useSelector(
    (state) => state.customers
  );

  useEffect(() => {
    const savedPage = location.state?.page || 1;
    const savedRows = location.state?.rows || 5;
    const savedInitialValue = location.state?.initialValue || 1;

    setpage(savedPage);
    setRows(savedRows);
    setInitialValue(savedInitialValue);

    store.dispatch(get_customer_list({ page: savedPage, rows: savedRows }));
  }, [location.state]);

  const filteredCustomers = customer_list.filter((el) =>
    el.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const onPrevious = () => {
    if (page > 1) {
      setInitialValue(Number(initialValue) - Number(rows));
      setpage(page - 1);
      store.dispatch(get_customer_list({ page: page - 1, rows }));
    }
  };

  const onNext = () => {
    if (rows * page < total_customer_count) {
      setInitialValue(Number(initialValue) + Number(rows));
      setpage(page + 1);
      store.dispatch(get_customer_list({ page: page + 1, rows }));
    }
  };

  const handleViewDetails = (el) => {
    navigate("/customer-list/customer-detail", {
      state: {
        customerId: el.id,
        branches: el.children,
        page,
        rows,
        initialValue,
      },
    });
  };

  // Helper to get initials from name
  const getInitials = (name) => {
    if (!name) return "C";
    const parts = name.trim().split(" ");
    return parts.length >= 2
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : parts[0][0].toUpperCase();
  };

  // Avatar colors pool
  const avatarColors = [
    "#007bc9",
    // "#6c63ff",
    // "#f37022",
    // "#2ea44f",
    // "#e53e3e",
    // "#805ad5",
    // "#d69e2e",
    // "#38a169",
  ];
  const getAvatarColor = (id) => avatarColors[id % avatarColors.length];

  return (
    <>
      {/* Header Bar */}
      <Card
        bg="#fff"
        paddingInline={5}
        paddingBlock={3}
        display="flex"
        flexDirection={["column", "column", "row"]}
        alignItems={["stretch", "stretch", "center"]}
        gap={3}
        boxShadow="sm"
        mb={1}
      >
        <Flex align="center" gap={2}>
          <Box
            w="38px"
            h="38px"
            borderRadius="10px"
            bg="blue.50"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FiUser} color="blue.500" boxSize={5} />
          </Box>
          <Box>
            <Heading fontSize="lg" color="gray.800">
              Customer List
            </Heading>
            <Text fontSize="xs" color="gray.400">
              {total_customer_count} total customers
            </Text>
          </Box>
        </Flex>

        <Spacer display={["none", "none", "block"]} />

        {/* Search Input */}
        <InputGroup bg="#f7f8fa" maxW="sm" borderRadius={8} w={["100%", "100%", "auto"]}>
          <Input
            placeholder="Search by customer name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            border="1.5px solid"
            borderColor="gray.200"
            _focus={{ borderColor: "#007bc9", boxShadow: "0 0 0 2px #007bc920" }}
            borderRadius={8}
            fontSize="sm"
          />
          <InputRightElement color="gray.400">
            <FiSearch />
          </InputRightElement>
        </InputGroup>
      </Card>

      {loading ? (
        <GasCylinderLoader size="sm" loadingText="Loading Customers" />
      ) : (
        <>
          <SimpleGrid minChildWidth="300px" spacing="16px" mt={4}>
            {filteredCustomers.length > 0 ? (
              filteredCustomers.map((el, i) => (
                <Card
                  key={el.id}
                  bg="#fff"
                  borderRadius="14px"
                  boxShadow="0 2px 8px rgba(0,0,0,0.07)"
                  border="1.5px solid"
                  borderColor="gray.100"
                  overflow="hidden"
                  _hover={{
                    boxShadow: "0 8px 24px rgba(0,123,201,0.13)",
                    borderColor: "blue.200",
                    transform: "translateY(-3px)",
                  }}
                  transition="all 0.22s ease"
                  // cursor="pointer"
                  // onClick={() => handleViewDetails(el)}
                  display="flex"
                  flexDirection="column"
                >
                  {/* Card Top Strip */}
                  <Box
                    h="3px"
                    bg={getAvatarColor(i)}
                    borderTopRadius="12px"
                  />

                  {/* Card Body */}
                  <Box px={5} pt={4} pb={3} flex={1}>
                    {/* Avatar + Name Row */}
                    <Flex align="center" gap={3} mb={3}>
                      <Avatar
                        name={el.name}
                        bg={getAvatarColor(i)}
                        color="#fff"
                        size="md"
                        fontWeight="bold"
                        fontSize="md"
                      />
                      <Box flex={1} minW={0}>
                        <Text
                          fontWeight="700"
                          fontSize="sm"
                          color="gray.800"
                          noOfLines={1}
                        >
                          {el.name || "N/A"}
                        </Text>
                        <Flex align="center" gap={1} mt="2px">
                          <Badge
                            colorScheme="blue"
                            fontSize="10px"
                            borderRadius="6px"
                            px={2}
                            py="1px"
                          >
                            #{el.id}
                          </Badge>
                          {el.userid && (
                            <Badge
                              colorScheme="purple"
                              fontSize="10px"
                              borderRadius="6px"
                              px={2}
                              py="1px"
                            >
                              {el.userid}
                            </Badge>
                          )}
                        </Flex>
                      </Box>
                    </Flex>

                    <Divider mb={3} />

                    {/* Details */}
                    <Flex direction="column" gap={2}>
                      {el.mobile && (
                        <Flex align="center" gap={2}>
                          <Box color="blue.400" flexShrink={0}>
                            <FiPhone size={13} />
                          </Box>
                          <Text fontSize="xs" color="gray.600" noOfLines={1}>
                            {el.mobile}
                          </Text>
                        </Flex>
                      )}
                      {el.email && (
                        <Flex align="center" gap={2}>
                          <Box color="green.400" flexShrink={0}>
                            <FiMail size={13} />
                          </Box>
                          <Text fontSize="xs" color="gray.600" noOfLines={1}>
                            {el.email}
                          </Text>
                        </Flex>
                      )}
                      {el.address && (
                        <Flex align="center" gap={2}>
                          <Box color="orange.400" flexShrink={0}>
                            <FiMapPin size={13} />
                          </Box>
                          <Text fontSize="xs" color="gray.600" noOfLines={1}>
                            {el.address}
                            {el.pincode ? ` - ${el.pincode}` : ""}
                          </Text>
                        </Flex>
                      )}
                    </Flex>

                    {/* GST / PAN small tags */}
                    {(el.gst || el.pan) && (
                      <Flex mt={3} gap={2} flexWrap="wrap">
                        {el.gst && (
                          <Badge
                            variant="subtle"
                            colorScheme="teal"
                            fontSize="10px"
                            borderRadius="6px"
                            px={2}
                          >
                            GST: {el.gst}
                          </Badge>
                        )}
                        {el.pan && (
                          <Badge
                            variant="subtle"
                            colorScheme="orange"
                            fontSize="10px"
                            borderRadius="6px"
                            px={2}
                          >
                            PAN: {el.pan}
                          </Badge>
                        )}
                      </Flex>
                    )}
                  </Box>

                  {/* View More Footer */}
                  <Box
                    px={5}
                    py={3}
                    bg="gray.50"
                    borderTop="1px solid"
                    borderColor="gray.100"
                    borderBottomRadius="12px"
                  >
                    <Flex align="center" justify="space-between">
                      <Text fontSize="xs" color="gray.400">
                        Click to view full profile
                      </Text>
                      <Button
                        size="xs"
                        colorScheme="blue"
                        variant="solid"
                        rightIcon={<FiArrowRight size={12} />}
                        borderRadius="8px"
                        px={3}
                        fontSize="xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(el);
                        }}
                      >
                        View More
                      </Button>
                    </Flex>
                  </Box>
                </Card>
              ))
            ) : (
              <Card p={10} textAlign="center" borderRadius="14px" borderColor="gray.100" border="1.5px solid">
                <Icon as={FiUser} color="gray.300" boxSize={10} mb={3} mx="auto" />
                <Text color="gray.500" fontWeight="600">No customers found</Text>
                <Text color="gray.400" fontSize="sm" mt={1}>
                  Try adjusting your search term
                </Text>
              </Card>
            )}
          </SimpleGrid>

          {/* Pagination */}
          <Card
            mt={4}
            alignItems="center"
            alignSelf="end"
            gap={3}
            flexDirection="row"
            borderRadius="12px"
            boxShadow="sm"
          >
            <Flex
              w="full"
              h="auto"
              justify={{ base: "center", md: "flex-end" }}
              alignItems="center"
              flexWrap="wrap"
              px={{ base: 4, md: 6 }}
              py={3}
              gap={4}
            >
              <Flex align="center" gap={2}>
                <Text fontSize="sm" color="gray.500">
                  Rows per page:
                </Text>
                <Select
                  size="sm"
                  maxW="70px"
                  value={rows}
                  borderRadius="8px"
                  onChange={(e) => {
                    store.dispatch(
                      get_customer_list({ page: page, rows: e.target.value })
                    );
                    setRows(e.target.value);
                  }}
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </Select>
              </Flex>

              <Flex gap={1} align="center" fontSize="sm" color="gray.600">
                <Text fontWeight="600">{initialValue}</Text>
                <Text>–</Text>
                <Text fontWeight="600">
                  {rows * page >= total_customer_count ? total_customer_count : rows * page}
                </Text>
                <Text color="gray.400">of</Text>
                <Text fontWeight="600">{total_customer_count}</Text>
              </Flex>

              <Flex gap={1} align="center">
                <Box
                  as="button"
                  w="32px"
                  h="32px"
                  borderRadius="8px"
                  border="1.5px solid"
                  borderColor={page === 1 ? "gray.200" : "blue.300"}
                  bg={page === 1 ? "gray.50" : "blue.50"}
                  color={page === 1 ? "gray.400" : "#007bc9"}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  cursor={page === 1 ? "not-allowed" : "pointer"}
                  onClick={onPrevious}
                  _hover={page !== 1 ? { bg: "blue.100" } : {}}
                  transition="all 0.15s"
                >
                  <FiChevronLeft size={18} />
                </Box>
                <Box
                  as="button"
                  w="32px"
                  h="32px"
                  borderRadius="8px"
                  border="1.5px solid"
                  borderColor={rows * page >= total_customer_count ? "gray.200" : "blue.300"}
                  bg={rows * page >= total_customer_count ? "gray.50" : "blue.50"}
                  color={rows * page >= total_customer_count ? "gray.400" : "#007bc9"}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  cursor={rows * page >= total_customer_count ? "not-allowed" : "pointer"}
                  onClick={onNext}
                  _hover={rows * page < total_customer_count ? { bg: "blue.100" } : {}}
                  transition="all 0.15s"
                >
                  <FiChevronRight size={18} />
                </Box>
              </Flex>
            </Flex>
          </Card>
        </>
      )}
    </>
  );
};

export default CustomerList;
